/* eslint-disable max-len */
import type { AnyObjectSchema } from 'yup';
import { isDistrictAdmin, isStateAdmin } from '../../utilities/userUtilities';
import { DataEntryMode } from '../DataEntryMode';
import { VALIDATION_MESSAGE } from '../ValidationMessage';
import { createDateSchema } from './CustomValidation/DateExtendedSchema';
import yup from './CustomValidation/yup-extended';
import { ExtendedDateSchemaType } from './ValidationTypes';

const createSchoolManagementValidationSchema = (
  dataEntryMode: DataEntryMode = DataEntryMode.NEW,
  existingSchoolIdentifiers: string[] = []
): AnyObjectSchema =>
  yup.object().shape(
    {
      schoolName: yup
        .string()
        .required(VALIDATION_MESSAGE.REQUIRED)
        .min(1, VALIDATION_MESSAGE.EXACT_LENGTH(1))
        .max(60, VALIDATION_MESSAGE.AT_MOST_CHARS(60)),
      schoolType: yup
        .string()
        .required(VALIDATION_MESSAGE.REQUIRED) // `schoolType` is required.
        .when(
          'schoolIdentifierSchoolPart',
          (schoolIdentifierSchoolPart, schema) => {
            switch (schoolIdentifierSchoolPart) {
              case '500':
                return schema.oneOf(
                  ['Special Education School'],
                  'If school ID is 500, School Type must be Special Education School'
                );
              case '200':
                return schema.oneOf(
                  ['Career And Technical Education School'],
                  'If school ID is 200, School Type must be Career and Technical Education School'
                );
              default:
                return schema;
            }
          }
        ),
      reportedFederally: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
      actIdentifier: yup
        .string()
        .mustBeNumber()
        .max(50, VALIDATION_MESSAGE.AT_MOST_CHARS(50)),
      ncesIdentifier: yup
        .string()
        .mustBeNumber()
        .min(7, VALIDATION_MESSAGE.MUST_BE_EXACT_NUMBER_LENGTH(7))
        .max(7, VALIDATION_MESSAGE.AT_MOST_CHARS(7)),
      charterSchool: yup.string().notRequired(),
      charterSchoolType: yup
        .string()
        .required(VALIDATION_MESSAGE.REQUIRED)
        .when('charterSchool', {
          is: (charterSchool: string) => charterSchool === 'Yes',
          then: yup
            .string()
            .required(VALIDATION_MESSAGE.REQUIRED)
            .notOneOf(
              ['Not a Charter School'],
              'If charter school is YES, then Charter School Type cannot be Not a Charter School'
            ),
          otherwise: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
        }),
      schoolIdentifierSchoolPart: yup
        .string()
        .required(VALIDATION_MESSAGE.REQUIRED)
        .mustBeNumber()
        .min(3, VALIDATION_MESSAGE.EXACT_NUMBER_LENGTH(3))
        .max(3, VALIDATION_MESSAGE.AT_MOST_CHARS(3))
        .test(
          'school-identifier-does-not-exist',
          'School Identifier already exists for this LEA',
          function (value) {
            if (dataEntryMode !== DataEntryMode.NEW || !value) {
              return true;
            }

            const { schoolIdentifierLeaPart } = this.parent;
            const fullSchoolIdentifier =
              `${schoolIdentifierLeaPart}-${value}`.trim();

            return !existingSchoolIdentifiers
              .map((schoolIdentifier) => schoolIdentifier.trim())
              .includes(fullSchoolIdentifier);
          }
        ),
      schoolOperationalStatus: yup
        .string()
        .required(VALIDATION_MESSAGE.REQUIRED),
      operationalStatusEffectiveDate: (
        createDateSchema() as ExtendedDateSchemaType
      ).required(VALIDATION_MESSAGE.REQUIRED),
      grades: yup.array(),
      website: yup.string().max(300, VALIDATION_MESSAGE.AT_MOST_CHARS(300)),
      phone: yup
        .string()
        .required(VALIDATION_MESSAGE.REQUIRED)
        .matches(
          /^[1-9]\d{2}-\d{3}-\d{4}$/,
          VALIDATION_MESSAGE.MUST_BE_IN_PHONE_NUMBER_FORMAT
        ),
      physicalAddressStreetNumberAndName: yup
        .string()
        .max(150, VALIDATION_MESSAGE.AT_MOST_CHARS(150))
        .ensure()
        .when(['physicalCity', 'physicalState', 'physicalZipCode'], {
          is: (
            physicalCity: string,
            physicalState: string,
            physicalZipCode: string
          ) =>
            physicalCity !== '' ||
            physicalState !== '' ||
            physicalZipCode !== '',
          then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
          otherwise: yup.string().notRequired(),
        }),
      physicalAptSuiteBuildingFloorNumber: yup
        .string()
        .max(60, VALIDATION_MESSAGE.AT_MOST_CHARS(60)),
      physicalCity: yup
        .string()
        .max(30, VALIDATION_MESSAGE.AT_MOST_CHARS(30))
        .ensure()
        .when(
          [
            'physicalAddressStreetNumberAndName',
            'physicalState',
            'physicalZipCode',
          ],
          {
            is: (
              physicalAddressStreetNumberAndName: string,
              physicalState: string,
              physicalZipCode: string
            ) =>
              physicalAddressStreetNumberAndName !== '' ||
              physicalState !== '' ||
              physicalZipCode !== '',
            then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
            otherwise: yup.string().notRequired(),
          }
        ),
      physicalState: yup
        .string()
        .ensure()
        .when(
          [
            'physicalAddressStreetNumberAndName',
            'physicalCity',
            'physicalZipCode',
          ],
          {
            is: (
              physicalAddressStreetNumberAndName: string,
              physicalCity: string,
              physicalZipCode: string
            ) =>
              physicalAddressStreetNumberAndName !== '' ||
              physicalCity !== '' ||
              physicalZipCode !== '',
            then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
            otherwise: yup.string().notRequired(),
          }
        ),
      physicalZipCode: yup
        .string()
        .max(17, VALIDATION_MESSAGE.AT_MOST_CHARS(17))
        .ensure()
        .when(
          [
            'physicalAddressStreetNumberAndName',
            'physicalCity',
            'physicalState',
          ],
          {
            is: (
              physicalAddressStreetNumberAndName: string,
              physicalCity: string,
              physicalState: string
            ) =>
              physicalAddressStreetNumberAndName !== '' ||
              physicalCity !== '' ||
              physicalState !== '',
            then: yup
              .string()
              .required(VALIDATION_MESSAGE.REQUIRED)
              .matches(
                /^[0-9\-]{5,17}$/,
                VALIDATION_MESSAGE.MUST_BE_IN_ZIP_CODE_FORMAT
              ),
            otherwise: yup.string().notRequired(),
          }
        ),
      physicalCounty: yup
        .string()
        .ensure()
        .when(['physicalState'], {
          is: (physicalState: string) => physicalState === 'Mississippi',
          then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
          otherwise: yup.string().notRequired(),
        }),
      mailingAddressStreetNumberAndName: yup
        .string()
        .max(150, VALIDATION_MESSAGE.AT_MOST_CHARS(150))
        .ensure()
        .when(['mailingCity', 'mailingState', 'mailingZipCode'], {
          is: (
            mailingCity: string,
            mailingState: string,
            mailingZipCode: string
          ) =>
            mailingCity !== '' || mailingState !== '' || mailingZipCode !== '',
          then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
          otherwise: yup.string().notRequired(),
        }),
      mailingAptSuiteBuildingFloorNumber: yup
        .string()
        .max(60, VALIDATION_MESSAGE.AT_MOST_CHARS(60)),
      mailingCity: yup
        .string()
        .max(30, VALIDATION_MESSAGE.AT_MOST_CHARS(30))
        .ensure()
        .when(
          [
            'mailingAddressStreetNumberAndName',
            'mailingState',
            'mailingZipCode',
          ],
          {
            is: (
              mailingAddressStreetNumberAndName: string,
              mailingState: string,
              mailingZipCode: string
            ) =>
              mailingAddressStreetNumberAndName !== '' ||
              mailingState !== '' ||
              mailingZipCode !== '',
            then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
            otherwise: yup.string().notRequired(),
          }
        ),
      mailingState: yup
        .string()
        .ensure()
        .when(
          [
            'mailingAddressStreetNumberAndName',
            'mailingCity',
            'mailingZipCode',
          ],
          {
            is: (
              mailingAddressStreetNumberAndName: string,
              mailingCity: string,
              mailingZipCode: string
            ) =>
              mailingAddressStreetNumberAndName !== '' ||
              mailingCity !== '' ||
              mailingZipCode !== '',
            then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
            otherwise: yup.string().notRequired(),
          }
        ),
      mailingZipCode: yup
        .string()
        .max(17, VALIDATION_MESSAGE.AT_MOST_CHARS(17))
        .ensure()
        .when(
          ['mailingAddressStreetNumberAndName', 'mailingCity', 'mailingState'],
          {
            is: (
              mailingAddressStreetNumberAndName: string,
              mailingCity: string,
              mailingState: string
            ) =>
              mailingAddressStreetNumberAndName !== '' ||
              mailingCity !== '' ||
              mailingState !== '',
            then: yup
              .string()
              .required(VALIDATION_MESSAGE.REQUIRED)
              .matches(
                /^[0-9\-]{5,17}$/,
                VALIDATION_MESSAGE.MUST_BE_IN_ZIP_CODE_FORMAT
              ),
            otherwise: yup.string().notRequired(),
          }
        ),
      mailingCounty: yup
        .string()
        .ensure()
        .when(['mailingState'], {
          is: (mailingState: string) => mailingState === 'Mississippi',
          then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
          otherwise: yup.string().notRequired(),
        }),
      scheduleType: (() => {
        if (dataEntryMode === DataEntryMode.NEW) {
          return yup
            .string()
            .when(['schoolType', 'schoolIdentifierSchoolPart'], {
              is: (schoolType: string, schoolId: string) =>
                schoolType === 'Regular School' &&
                !['001', '200', '500'].includes(schoolId),
              then: yup
                .string()
                .test(
                  'schedule-type-required-create',
                  VALIDATION_MESSAGE.REQUIRED,
                  function (value) {
                    return isStateAdmin() ? true : !!value;
                  }
                ),
              otherwise: yup.string().notRequired(),
            });
        }
        return yup.string().when(['schoolType', 'schoolIdentifierSchoolPart'], {
          is: (schoolType: string, schoolId: string) =>
            schoolType === 'Regular School' &&
            !['001', '200', '500'].includes(schoolId),
          then: yup
            .string()
            .test(
              'schedule-type-required-edit',
              VALIDATION_MESSAGE.REQUIRED,
              function (value) {
                return isDistrictAdmin() ? !!value : true;
              }
            ),
          otherwise: yup.string().notRequired(),
        });
      })(),
      schoolContact: (() => {
        if (dataEntryMode === DataEntryMode.NEW) {
          return yup
            .object()
            .when(['schoolType', 'schoolIdentifierSchoolPart'], {
              is: (schoolType: string, schoolId: string) =>
                schoolType === 'Regular School' &&
                !['001', '200', '500'].includes(schoolId),
              then: yup.object().shape({
                firstName: yup
                  .string()
                  .test(
                    'msis-contact-first-name-required-create',
                    VALIDATION_MESSAGE.REQUIRED,
                    function (value) {
                      return isStateAdmin() ? true : !!value;
                    }
                  ),
                lastorSurname: yup
                  .string()
                  .test(
                    'msis-contact-last-name-required-create',
                    VALIDATION_MESSAGE.REQUIRED,
                    function (value) {
                      return isStateAdmin() ? true : !!value;
                    }
                  ),
                email: yup
                  .string()
                  .notRequired()
                  .test(
                    'msis-contact-email-length-create',
                    VALIDATION_MESSAGE.MUST_BE_IN_BETWEEN_RANGE(7, 128),
                    function (value) {
                      if (!value) {
                        return true;
                      }
                      return value.length >= 7 && value.length <= 128;
                    }
                  )
                  .test(
                    'msis-contact-email-format-create',
                    VALIDATION_MESSAGE.MUST_BE_IN_VALID_EMAIL_FORMAT,
                    function (value) {
                      if (!value) {
                        return true;
                      }
                      return yup.string().email().isValidSync(value);
                    }
                  ),
                positionTitle: yup.string().notRequired(),
                telephoneNumber: yup
                  .string()
                  .test(
                    'msis-contact-phone-required-create',
                    VALIDATION_MESSAGE.REQUIRED,
                    function (value) {
                      if (isStateAdmin()) {
                        return true;
                      }
                      if (!value) {
                        return false;
                      }
                      return /^[1-9]\d{2}-\d{3}-\d{4}$/.test(value);
                    }
                  )
                  .test(
                    'msis-contact-phone-format-create',
                    VALIDATION_MESSAGE.MUST_BE_IN_PHONE_NUMBER_FORMAT,
                    function (value) {
                      if (!value) {
                        return true;
                      }
                      return /^[1-9]\d{2}-\d{3}-\d{4}$/.test(value);
                    }
                  ),
              }),
              otherwise: yup.object().shape({
                firstName: yup.string().transform(() => ''),
                lastorSurname: yup.string().transform(() => ''),
                email: yup.string().transform(() => ''),
                positionTitle: yup.string().transform(() => ''),
                telephoneNumber: yup.string().transform(() => ''),
              }),
            });
        }
        return yup.object().when(['schoolType', 'schoolIdentifierSchoolPart'], {
          is: (schoolType: string, schoolId: string) =>
            schoolType === 'Regular School' &&
            !['001', '200', '500'].includes(schoolId),
          then: yup.object().shape({
            firstName: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
            lastorSurname: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
            email: yup
              .string()
              .min(7, VALIDATION_MESSAGE.MUST_BE_IN_BETWEEN_RANGE(7, 128))
              .max(128, VALIDATION_MESSAGE.MUST_BE_IN_BETWEEN_RANGE(7, 128))
              .email(VALIDATION_MESSAGE.MUST_BE_IN_VALID_EMAIL_FORMAT),
            positionTitle: yup.string(),
            telephoneNumber: yup
              .string()
              .required(VALIDATION_MESSAGE.REQUIRED)
              .matches(
                /^[1-9]\d{2}-\d{3}-\d{4}$/,
                VALIDATION_MESSAGE.MUST_BE_IN_PHONE_NUMBER_FORMAT
              ),
          }),
          otherwise: yup.object().shape({
            firstName: yup.string().transform(() => ''),
            lastorSurname: yup.string().transform(() => ''),
            email: yup.string().transform(() => ''),
            positionTitle: yup.string().transform(() => ''),
            telephoneNumber: yup.string().transform(() => ''),
          }),
        });
      })(),
    },
    [
      ['mailingAddressStreetNumberAndName', 'mailingCity'],
      ['mailingAddressStreetNumberAndName', 'mailingState'],
      ['mailingAddressStreetNumberAndName', 'mailingZipCode'],
      ['mailingCity', 'mailingState'],
      ['mailingState', 'mailingZipCode'],
      ['mailingCity', 'mailingZipCode'],
      ['physicalAddressStreetNumberAndName', 'physicalCity'],
      ['physicalAddressStreetNumberAndName', 'physicalState'],
      ['physicalAddressStreetNumberAndName', 'physicalZipCode'],
      ['physicalCity', 'physicalState'],
      ['physicalState', 'physicalZipCode'],
      ['physicalCity', 'physicalZipCode'],
    ]
  );

export const SchoolManagementValidationSchema =
  createSchoolManagementValidationSchema(DataEntryMode.NEW);
export const SchoolManagementValidationEditSchema =
  createSchoolManagementValidationSchema(DataEntryMode.EDIT);
export const getSchoolManagementValidationSchema = (
  existingSchoolIdentifiers: string[] = []
): AnyObjectSchema =>
  createSchoolManagementValidationSchema(
    DataEntryMode.NEW,
    existingSchoolIdentifiers
  );
